// SS PWA — lib/api/diagnostics.ts — 2026-06-03
//
// Typed client for the Worker /admin/diagnostics/* endpoints. Mirrors
// src/handlers/diagnostics/lib/outputSchema.ts in the Worker repo.
//
// Auth: reuses the same Cloudflare Access session that the rest of the
// PWA uses. The Worker handlers accept Bearer DIAGNOSTICS_TOKEN OR
// AUDIT_TOKEN OR per-slug PIT. In the PWA we proxy via Access by reading
// VITE_DIAGNOSTICS_TOKEN from the build env (no secret in the browser
// bundle — operator pastes the token into a settings field at runtime
// and we keep it in sessionStorage). See `setDiagnosticsToken`.

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? 'https://api.servicesteady.com';

const TOKEN_KEY = 'ss-diagnostics-token';

export function setDiagnosticsToken(token: string): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(TOKEN_KEY, token);
  }
}

export function getDiagnosticsToken(): string {
  if (typeof sessionStorage === 'undefined') return '';
  return sessionStorage.getItem(TOKEN_KEY) ?? '';
}

export function clearDiagnosticsToken(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(TOKEN_KEY);
  }
}

export class DiagnosticsApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'DiagnosticsApiError';
    this.status = status;
    this.body = body;
  }
}

// -------------------- Response shapes (mirror outputSchema.ts) --------------

export type Severity = 'P0' | 'P1' | 'P2' | 'P3';

export interface AnomalyEvidence {
  rule: string;
  severity: Severity;
  evidence: Record<string, unknown>;
}

export interface AnomalySummary {
  by_severity: Record<Severity, number>;
  by_rule: Record<string, number>;
  total: number;
}

export interface PerCallAudit {
  call_id: string;
  contact_id?: string;
  phone?: string;
  outcome?: string;
  start_ts?: number;
  end_ts?: number;
  duration_sec?: number;
  steps: Record<string, 'pass' | 'fail' | 'unknown' | 'skip'>;
  anomalies: AnomalyEvidence[];
  classification: 'HAPPY' | 'ANOMALY';
}

export interface PerSmsAudit {
  conversation_id: string;
  contact_id?: string;
  phone?: string;
  last_message_at?: number;
  inbound_count: number;
  outbound_count: number;
  steps: Record<string, 'pass' | 'fail' | 'unknown' | 'skip'>;
  anomalies: AnomalyEvidence[];
  classification: 'HAPPY' | 'ANOMALY';
}

export interface PerQuoteAudit {
  contact_id: string;
  phone?: string;
  quote_amount: number | null;
  service_type: string | null;
  quoted_at?: string | null;
  steps: Record<string, 'pass' | 'fail' | 'unknown' | 'skip'>;
  anomalies: AnomalyEvidence[];
  classification: 'HAPPY' | 'ANOMALY';
}

export interface AuditResponse {
  tenant: {
    slug: string;
    display_name: string;
    location_id: string;
    timezone: string;
  };
  window: { start: string; end: string; hours: number; depth: string };
  summary: {
    calls_audited: number;
    sms_threads_audited: number;
    quotes_audited: number;
    anomalies: AnomalySummary;
  };
  calls: PerCallAudit[];
  sms_threads: PerSmsAudit[];
  quotes: PerQuoteAudit[];
  cache_health: { field_map_present: boolean; push_subs_present: number };
  system_health: {
    counters: Record<string, number>;
    heartbeats: Record<string, string | null>;
    minutes_since_last_call: number | null;
    minutes_since_last_sms_in: number | null;
    minutes_since_last_push: number | null;
  };
  config_drift: string[];
  raw_anomaly_list: AnomalyEvidence[];
  generated_at: string;
}

export interface HealthResponse {
  tenant: { slug: string; display_name: string; location_id: string };
  status: 'green' | 'yellow' | 'red';
  reasons: string[];
  counters_last_hour: Record<string, number>;
  heartbeats: Record<string, string | null>;
  minutes_since: Record<string, number | null>;
  bindings: {
    db: boolean;
    sms_state_kv: boolean;
    push_subs_kv: boolean;
    pit_present: boolean;
  };
  config_drift: string[];
  generated_at: string;
}

export interface ReplayEvent {
  ts: string;
  kind: string;
  step: string;
  status: 'ok' | 'warn' | 'error' | 'skip' | 'noop';
  source: 'retell' | 'ghl' | 'kv' | 'd1' | 'worker' | 'pwa';
  detail: Record<string, unknown>;
}

export interface ReplayResponse {
  scope: 'contact' | 'call';
  scope_id: string;
  tenant: { slug: string; display_name: string };
  events: ReplayEvent[];
  partial?: boolean;
}

export interface SyntheticRecord {
  trace_id: string;
  slug: string;
  type: string;
  status: 'queued' | 'dispatched' | 'verified' | 'failed';
  detail: Record<string, unknown>;
  generated_at: string;
}

export interface TenantListItem {
  slug: string;
  display_name: string;
  location_id: string;
  timezone: string;
}

// -------------------- Internals --------------------

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getDiagnosticsToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new DiagnosticsApiError(res.status, `HTTP ${res.status}`, body);
  }
  return (await res.json()) as T;
}

// -------------------- Exports --------------------

export async function listTenants(): Promise<TenantListItem[]> {
  const r = await request<{ tenants: TenantListItem[] }>(
    '/admin/diagnostics/tenants',
  );
  return r.tenants;
}

export function fetchAudit(
  slug: string,
  opts: { hours?: number; depth?: string } = {},
): Promise<AuditResponse> {
  const params = new URLSearchParams({ slug });
  if (opts.hours) params.set('hours', String(opts.hours));
  if (opts.depth) params.set('depth', opts.depth);
  return request<AuditResponse>(`/admin/diagnostics/audit?${params.toString()}`);
}

export function fetchHealth(slug: string): Promise<HealthResponse> {
  const params = new URLSearchParams({ slug });
  return request<HealthResponse>(`/admin/diagnostics/health?${params.toString()}`);
}

export function fetchAnomalies(
  slug: string,
  opts: { hours?: number; rule?: string } = {},
): Promise<AuditResponse> {
  const params = new URLSearchParams({ slug });
  if (opts.hours) params.set('hours', String(opts.hours));
  if (opts.rule) params.set('rule', opts.rule);
  return request<AuditResponse>(`/admin/diagnostics/anomalies?${params.toString()}`);
}

export function fetchReplayContact(
  slug: string,
  contactId: string,
): Promise<ReplayResponse> {
  const params = new URLSearchParams({ slug });
  return request<ReplayResponse>(
    `/admin/diagnostics/replay/${encodeURIComponent(contactId)}?${params.toString()}`,
  );
}

export function fetchReplayCall(
  slug: string,
  callId: string,
): Promise<ReplayResponse> {
  const params = new URLSearchParams({ slug });
  return request<ReplayResponse>(
    `/admin/diagnostics/replay/call/${encodeURIComponent(callId)}?${params.toString()}`,
  );
}

export function fetchReconcile(
  slug: string,
  hours = 72,
): Promise<unknown> {
  const params = new URLSearchParams({ slug, hours: String(hours) });
  return request<unknown>(`/admin/diagnostics/reconcile?${params.toString()}`);
}

export function postSynthetic(
  slug: string,
  type: 'call' | 'sms' | 'push' | 'workflow_trigger',
): Promise<{ trace_id: string; record: SyntheticRecord }> {
  return request<{ trace_id: string; record: SyntheticRecord }>(
    '/admin/diagnostics/synthetic',
    { method: 'POST', body: JSON.stringify({ slug, type }) },
  );
}

export function fetchSynthetic(
  slug: string,
  traceId: string,
): Promise<SyntheticRecord> {
  const params = new URLSearchParams({ slug });
  return request<SyntheticRecord>(
    `/admin/diagnostics/synthetic/${encodeURIComponent(traceId)}?${params.toString()}`,
  );
}
